import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  SUPPORT_CATEGORY_LABEL,
  getStatusLabel,
  type SupportMessageDto,
  type SupportTicketDto,
  type SupportTicketStatus,
} from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';

// Mirror the admin Support list. Server is authoritative on RBAC, but the UI
// hides write affordances for roles that can't use them so the staffer
// doesn't bounce off a 403.
const SUPPORT_WRITE_ROLES = new Set(['super', 'admin', 'lead', 'analyst']);

const ROLE_LABELS: Record<string, string> = {
  super: 'Super Admin', admin: 'Admin', lead: 'Engagement Lead',
  analyst: 'Analyst', compliance: 'Compliance', finance: 'Finance', readonly: 'Read-only',
};

const STATUS_TONE: Record<SupportTicketStatus, string> = {
  open:            'amber',
  acknowledged:    'accent',
  in_progress:     'accent',
  awaiting_client: 'amber',
  resolved:        'green',
  closed:          '',
};

// Server-enforced state machine — UI rendering mirrors PLAN §4. Buttons
// for illegal transitions are simply not rendered.
const NEXT_STATES: Partial<Record<SupportTicketStatus, { to: SupportTicketStatus; label: string }[]>> = {
  acknowledged:    [{ to: 'in_progress',     label: 'Start work' }],
  in_progress:     [
    { to: 'awaiting_client', label: 'Mark awaiting client' },
    { to: 'resolved',        label: 'Mark resolved' },
  ],
  awaiting_client: [
    { to: 'in_progress', label: 'Back to in progress' },
    { to: 'resolved',    label: 'Mark resolved' },
  ],
};

function formatStamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 16).replace('T', ' ');
}

const REPLY_MAX = 5000;

export function AdminSupportDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [ticket, setTicket] = useState<SupportTicketDto | null>(null);
  const [messages, setMessages] = useState<SupportMessageDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState<SupportTicketStatus | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const role = user?.role ?? '';
  const canWrite = SUPPORT_WRITE_ROLES.has(role);

  // Initial fetch — the GET /:id call is what triggers the open→acknowledged
  // auto-flip on the server, so the ticket we see is already post-flip.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([
      apiGet<{ ticket: SupportTicketDto }>(`/api/support/${id}`),
      apiGet<{ messages: SupportMessageDto[] }>(`/api/support/${id}/messages`),
    ])
      .then(([t, m]) => {
        if (cancelled) return;
        setTicket(t.ticket);
        setMessages(m.messages);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.serverMessage : 'Could not load ticket.');
      });
    return () => { cancelled = true; };
  }, [id]);

  // Keep scroll pinned to bottom whenever the thread grows.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const isResolved = ticket?.status === 'resolved';
  const nextStates = useMemo(
    () => (ticket ? NEXT_STATES[ticket.status] ?? [] : []),
    [ticket],
  );

  async function handleClaim() {
    if (!ticket || claiming || !canWrite) return;
    setClaiming(true);
    setTransitionError(null);
    try {
      const r = await apiPost<{ ticket: SupportTicketDto }>(`/api/support/${ticket.id}/claim`, {});
      setTicket(r.ticket);
    } catch (err) {
      setTransitionError(err instanceof ApiError ? err.serverMessage : 'Could not claim ticket.');
    } finally {
      setClaiming(false);
    }
  }

  async function handleTransition(to: SupportTicketStatus) {
    if (!ticket || transitioning || !canWrite) return;
    setTransitioning(to);
    setTransitionError(null);
    try {
      const r = await apiPost<{ ticket: SupportTicketDto }>(
        `/api/support/${ticket.id}/status`,
        { status: to },
      );
      setTicket(r.ticket);
    } catch (err) {
      setTransitionError(err instanceof ApiError ? err.serverMessage : 'Could not change status.');
    } finally {
      setTransitioning(null);
    }
  }

  async function handleSendReply() {
    if (!ticket || sending || !canWrite) return;
    const body = reply.trim();
    if (body.length < 1 || body.length > REPLY_MAX) return;
    if (isResolved) return;
    setSending(true);
    setReplyError(null);
    try {
      const r = await apiPost<{ message: SupportMessageDto; ticket: SupportTicketDto }>(
        `/api/support/${ticket.id}/messages`,
        { body },
      );
      setMessages((prev) => [...(prev ?? []), r.message]);
      setTicket(r.ticket);
      setReply('');
    } catch (err) {
      setReplyError(err instanceof ApiError ? err.serverMessage : 'Could not send reply.');
    } finally {
      setSending(false);
    }
  }

  if (loadError) {
    return (
      <>
        <h1 className="portal-h1">Ticket not found</h1>
        <p className="portal-sub">{loadError}</p>
        <Link className="btn ghost" to="/admin/support" style={{ marginTop: 24 }}>← Support</Link>
      </>
    );
  }
  if (!ticket || messages === null) return <p className="portal-sub">Loading…</p>;

  const ownsThis = ticket.owner_user_id !== null && ticket.owner_user_id === user?.id;
  const replyDisabled = !canWrite || isResolved || sending || reply.trim().length < 1 || reply.length > REPLY_MAX;

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--fg-mute)', marginBottom: 8 }}>
        <Link to="/admin/support" style={{ borderBottom: '1px solid var(--rule)' }}>← Support</Link>
        {' / '}<span className="rid">{ticket.id}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="portal-h1">{ticket.subject}</h1>
          <p className="portal-sub">
            <span className="rid">{ticket.id}</span> · {SUPPORT_CATEGORY_LABEL[ticket.category]} · {ticket.client_id}
          </p>
        </div>
        <span className={`chip ${STATUS_TONE[ticket.status]}`}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
          {getStatusLabel(ticket.status, 'staff')}
        </span>
      </div>

      <div className="proposal-form" style={{ alignItems: 'start', marginTop: 20 }}>
        {/* LEFT — facts + actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <h3 className="proposal-section-h">Details</h3>
            <div className="proposal-kv"><span>Ref</span><strong><span className="rid">{ticket.id}</span></strong></div>
            <div className="proposal-kv"><span>Category</span><strong>{SUPPORT_CATEGORY_LABEL[ticket.category]}</strong></div>
            {ticket.related_ref && (
              <div className="proposal-kv"><span>Related ref</span><strong><span className="rid">{ticket.related_ref}</span></strong></div>
            )}
            <div className="proposal-kv"><span>Client</span><strong><span className="rid">{ticket.client_id}</span></strong></div>
            <div className="proposal-kv"><span>Opened</span><strong>{formatStamp(ticket.created_at)}</strong></div>
            <div className="proposal-kv"><span>Acknowledged</span><strong>{formatStamp(ticket.acknowledged_at)}</strong></div>
            <div className="proposal-kv"><span>Last staff reply</span><strong>{formatStamp(ticket.last_staff_reply_at)}</strong></div>
            <div className="proposal-kv"><span>Last client reply</span><strong>{formatStamp(ticket.last_client_reply_at)}</strong></div>
            {ticket.resolved_at && (
              <div className="proposal-kv"><span>Resolved</span><strong>{formatStamp(ticket.resolved_at)}</strong></div>
            )}
          </div>

          <div className="card">
            <h3 className="proposal-section-h">Original message</h3>
            <div
              style={{
                background: 'var(--bg-soft)',
                padding: 14,
                borderRadius: 4,
                fontSize: 13.5,
                lineHeight: 1.6,
                color: 'var(--fg-soft)',
                whiteSpace: 'pre-wrap',
                borderLeft: '3px solid var(--rule)',
              }}
            >
              {ticket.body}
            </div>
          </div>

          <div className="card">
            <h3 className="proposal-section-h">Owner</h3>
            {ticket.owner_user_id === null && (
              <>
                <p style={{ fontSize: 13.5, color: 'var(--fg-soft)', margin: 0 }}>
                  No owner yet — claim to take it.
                </p>
                {canWrite && (
                  <div style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn accent"
                      onClick={handleClaim}
                      disabled={claiming}
                    >
                      {claiming ? 'Claiming…' : 'Claim ticket'}
                    </button>
                  </div>
                )}
              </>
            )}
            {ownsThis && (
              <p style={{ fontSize: 13.5, color: 'var(--fg)', margin: 0 }}>
                Handled by you.
              </p>
            )}
            {ticket.owner_user_id !== null && !ownsThis && (
              <p style={{ fontSize: 13.5, color: 'var(--fg-soft)', margin: 0 }}>
                Handled by <strong>{ticket.owner_name ?? '—'}</strong>
                {ticket.owner_role && <>, {ROLE_LABELS[ticket.owner_role] ?? ticket.owner_role}</>}.
              </p>
            )}
          </div>

          {canWrite && nextStates.length > 0 && (
            <div className="card">
              <h3 className="proposal-section-h">Move to</h3>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {nextStates.map((n) => (
                  <button
                    key={n.to}
                    type="button"
                    className="btn"
                    onClick={() => handleTransition(n.to)}
                    disabled={transitioning !== null}
                  >
                    {transitioning === n.to ? 'Working…' : n.label}
                  </button>
                ))}
              </div>
              {transitionError && (
                <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 10 }}>{transitionError}</div>
              )}
            </div>
          )}

          {canWrite && nextStates.length === 0 && ticket.status === 'resolved' && (
            <div className="card">
              <p style={{ fontSize: 13.5, color: 'var(--fg-soft)', margin: 0 }}>
                Resolved on {formatStamp(ticket.resolved_at)}.
              </p>
            </div>
          )}
        </div>

        {/* RIGHT — thread + reply */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', minHeight: 480 }}>
          <h3 className="proposal-section-h" style={{ marginTop: 0 }}>Conversation</h3>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '8px 0',
              maxHeight: 480,
            }}
          >
            {messages.length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--fg-mute)', textAlign: 'center', padding: 24 }}>
                No replies yet. Send the first one below.
              </p>
            )}
            {messages.map((m) => {
              const isStaff = m.author_kind === 'staff';
              return (
                <div
                  key={m.id}
                  style={{
                    padding: 12,
                    borderRadius: 4,
                    background: isStaff ? 'var(--bg-soft)' : 'transparent',
                    borderLeft: `3px solid ${isStaff ? 'var(--accent)' : 'var(--rule)'}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 12.5 }}>
                      <strong>{m.author_name}</strong>
                      <span style={{ color: 'var(--fg-mute)', marginLeft: 8, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10 }}>
                        {isStaff ? 'Staff' : 'Client'}
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                      {formatStamp(m.created_at)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', color: 'var(--fg)' }}>
                    {m.body}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply box */}
          <div style={{ marginTop: 14, borderTop: '1px solid var(--rule)', paddingTop: 14 }}>
            {isResolved && (
              <div
                style={{
                  background: 'var(--bg-soft)',
                  padding: 10,
                  borderRadius: 4,
                  fontSize: 13,
                  color: 'var(--fg-soft)',
                  marginBottom: 10,
                }}
              >
                Resolved on {formatStamp(ticket.resolved_at)}. Open a new ticket if needed.
              </div>
            )}
            {!canWrite && (
              <div
                style={{
                  background: 'var(--bg-soft)',
                  padding: 10,
                  borderRadius: 4,
                  fontSize: 13,
                  color: 'var(--fg-soft)',
                  marginBottom: 10,
                }}
              >
                Read-only for your role.
              </div>
            )}
            <textarea
              rows={4}
              className="prop-textarea"
              placeholder="Write a reply…"
              value={reply}
              disabled={!canWrite || isResolved || sending}
              maxLength={REPLY_MAX}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  void handleSendReply();
                }
              }}
            />
            {replyError && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 4 }}>{replyError}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                {reply.length} / {REPLY_MAX}
              </span>
              <button
                type="button"
                className="btn accent"
                onClick={handleSendReply}
                disabled={replyDisabled}
              >
                {sending ? 'Sending…' : 'Send reply'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
